import PlainDate from '@Domain/PlainDate';

export type DatedPayload = {
	readonly birthDate: string | null;
	readonly archivedAt: string | null;
	readonly purgeScheduledAt: string | null;
};

/**
 * `birthDate` is a calendar date, with no time of day and no timezone, so it is rebuilt from local
 * calendar components through `PlainDate.fromApi`, never through ISO-string parsing. `archivedAt` and `purgeScheduledAt` are
 * instants, for which straight ISO-to-`Date` conversion is correct. Any future dated field must pick
 * between these two before being added here.
 *
 * The return type spells out `Omit<Payload, …> & {…}` instead of the `Computed` helper: `Computed`
 * bounds its override to `Partial<Record<keyof Payload, unknown>>`, a constraint TypeScript cannot
 * verify against a still-generic `Payload`, even though `Payload extends DatedPayload` guarantees it.
 */
const computeBeneficiaryPayload = <Payload extends DatedPayload>(
	payload: Payload,
): Omit<Payload, 'birthDate' | 'archivedAt' | 'purgeScheduledAt'> & {
	readonly birthDate: PlainDate | null;
	readonly archivedAt: Date | null;
	readonly purgeScheduledAt: Date | null;
} => ({
	...payload,
	birthDate: payload.birthDate !== null ? PlainDate.fromApi(payload.birthDate) : null,
	archivedAt: payload.archivedAt !== null ? new Date(payload.archivedAt) : null,
	purgeScheduledAt: payload.purgeScheduledAt !== null ? new Date(payload.purgeScheduledAt) : null,
});

export default computeBeneficiaryPayload;
